import TableHeader from "@commons/Table/TableHeader";
import Table from "@commons/Table/TableContent";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { usePage } from "@hooks/usePage";
import ModalNewTaxonomicsGroup from "../components/ModalNewTaxonomicGroup";
import { useGetTaxonomicGroups } from "@services/taxonomicGroups/list";
import { toast } from "react-toastify";
import { useState } from "react";
import {
  taxonomicGroupColumns,
  type TaxonomicGroup,
} from "@typings/entities/taxonomicGroup";
import { config } from "@/config";
import { useExportTaxonomicGroups } from "@hooks/exports/useExportTaxonomicGroup";
import { usePermissions } from "@hooks/usePermissions";
import ModalEditTaxonomicGroups from "../components/ModalEditTaxonomicGroup";
import TextInput from "@commons/Input/Text";
import Button from "@commons/Button";
import Text from "@commons/Typography/Text";

function TaxonomicGroupsScreen() {
  const { openModal, setDataModal } = usePage();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const { can } = usePermissions();

  const { data, error, isLoading, mutate } = useGetTaxonomicGroups({
    page: page + 1,
    limit: rowsPerPage,
    search: searchQuery,
  });

  const { handleExport, isLoading: isExporting } = useExportTaxonomicGroups();

  const heads = taxonomicGroupColumns;

  const handleEdit = (taxonomicGroup: TaxonomicGroup) => {
    setDataModal({ id: taxonomicGroup.id });
    openModal("editTaxonomicGroup");
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setSearchQuery(search);
    setPage(0);
  };

  if (error) {
    toast.error("Erro ao carregar os grupos taxonômicos");
  }

  const canCreateTaxonomicGroup = can("create", "taxonomicGroups");
  const canUpdateTaxonomicGroup = can("update", "taxonomicGroups");

  return (
    <div className="w-full">
      <div className="w-full flex flex-col justify-center items-center text-center bg-white rounded-lg shadow-md p-4">
        <TableHeader.Root>
          <div className="flex gap-4 w-full sm:max-w-sm">
            <TextInput
              textSize="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Digite para buscar"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button
              startIcon={<SearchIcon />}
              customVariant="primary"
              size="md"
              onClick={handleSearch}
            >
              <Text>Filtrar</Text>
            </Button>
          </div>

          <div className="flex gap-x-4 gap-y-2 mt-2 flex-col xs:mt-0 xs:flex-row w-full xs:w-auto">
            {canCreateTaxonomicGroup && (
              <TableHeader.Button
                customVariant="secondary"
                startIcon={<AddIcon />}
                onClick={() => openModal("newTaxonomicGroup")}
              >
                Nova Espécie
              </TableHeader.Button>
            )}
            <TableHeader.Button
              customVariant="dark"
              onClick={() => handleExport()}
              disabled={isExporting}
            >
              {isExporting ? "Exportando..." : "Exportar .csv"}
            </TableHeader.Button>
          </div>
        </TableHeader.Root>

        <Table.Root isLoading={isLoading}>
          <Table.Head heads={heads} actions={canUpdateTaxonomicGroup} />
          <Table.Body
            rows={data?.items ?? []}
            columns={heads.map((head) => head.id)}
            actions={canUpdateTaxonomicGroup ? ["edit"] : []}
            onEdit={handleEdit}
          />
          <Table.Footer
            colSpan={heads.length}
            count={data?.meta.totalItems ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Table.Root>
      </div>
      <ModalNewTaxonomicsGroup onMutate={mutate} />
      <ModalEditTaxonomicGroups />
    </div>
  );
}

export default TaxonomicGroupsScreen;
